<script setup lang="ts">
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  hasAnalyticsConsent,
  persistAnalyticsConsentDecision,
} from '../../composables/useAnalyticsConsent'

const isVisible = ref(false)

onMounted(() => {
  if (hasAnalyticsConsent()) {
    isVisible.value = false
    return
  }

  try {
    const decision = globalThis.window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)
    isVisible.value = decision !== 'granted' && decision !== 'denied'
  } catch {
    isVisible.value = true
  }
})

function acceptAnalytics(): void {
  persistAnalyticsConsentDecision(true)
  isVisible.value = false
}

function rejectAnalytics(): void {
  persistAnalyticsConsentDecision(false)
  isVisible.value = false
}
</script>

<template>
  <aside
    v-if="isVisible"
    class="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur"
    role="region"
    aria-label="Cookie consent"
    data-testid="consent-banner"
  >
    <div class="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between">
      <p class="text-sm text-gray-700">
        We use essential cookies to run this site and optional analytics cookies to improve performance.
        See our
        <NuxtLink
          to="/privacy"
          class="font-medium text-brand-700 underline"
        >
          Privacy Policy
        </NuxtLink>,
        <NuxtLink
          to="/cookies"
          class="font-medium text-brand-700 underline"
        >
          Cookie Policy
        </NuxtLink>,
        and
        <NuxtLink
          to="/terms"
          class="font-medium text-brand-700 underline"
        >
          Terms of Service
        </NuxtLink>.
      </p>

      <div class="flex gap-2">
        <UButton
          class="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          data-testid="consent-reject"
          variant="ghost"
          @click="rejectAnalytics"
        >
          Reject analytics
        </UButton>
        <UButton
          class="rounded bg-brand-700 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          data-testid="consent-accept"
          color="primary"
          @click="acceptAnalytics"
        >
          Accept analytics
        </UButton>
      </div>
    </div>
  </aside>
</template>
